import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import Select from "react-select";
import SelectCreateAsync from "react-select/async-creatable";

import FotoField from "../general/FotoField";
import { bancos, formatCurrency, permisions, toast, validarPINSeguridad } from "../../utils/helpers";
import { apiRequest } from "../../hooks/apiRequest";
import Modal from "../general/Modal";
import { auth } from "../../utils/auth";
import DocumentoItem from "../Contratos/Form/Documentos";
import Seccion, { ErrorCampo } from "../general/Seccion";

/** Campo de fecha con el ícono de calendario delante. */
const CampoFecha = ({ name, defaultValue, readOnly }) => (
    <div className='input-group'>
        <div className='input-group-prepend'>
            <span className='input-group-text'>
                <i className='far fa-calendar' aria-hidden='true' />
            </span>
        </div>
        <input
            type='date'
            className='text-input form-control'
            defaultValue={defaultValue}
            name={name}
            readOnly={readOnly}
        />
    </div>
);

/** Valor de sólo lectura con el mismo alto que un input (sin permiso de edición). */
const SoloLectura = ({ children }) => (
    <div className="form-control" style={{backgroundColor: '#e9ecef', opacity: 1}}>
        &nbsp;{children}
    </div>
);

const Form = ({empleado, errors, tipos, vehiculos, codigo}) => {
    const [cargo, setCargo] = useState(empleado.id ? empleado.tipo : "");
    const [operaVehiculo, setOperaVehiculo] = useState(empleado.id ? empleado.operaVehiculo : false);
    const [activo, setActivo] = useState(empleado.id ? (empleado.active ? true : false) : true);
    const [documentos, setDocumentos] = useState([{id: 0}]);
    const [sueldo, setSueldo] = useState(empleado.sueldo || empleado.tipo?.sueldoFijo || 0);
    const [descuento, setDescuento] = useState(empleado.descuento || empleado.tipo?.descuento || 0);

    const [showPINModal, setShowPINModal] = useState(false);
    const [pinSeguridad, setPinSeguridad] = useState("");

    const puedeEditar = auth.canEdit(permisions.verEmpleados);

    const getTipo = (id) => {
        return tipos.filter((tipo) => tipo.id === parseInt(id))[0];
    };

    const onInputChange = (e) => {
        const tipo = getTipo(e.target.value);

        setCargo(tipo.nombre);
        setOperaVehiculo(tipo.operaVehiculo);
    };

    const getOptions = async (url = null, label = 'nombre', value = 'id') => {
        const table = await apiRequest({}, url, 'get');
        if (table.data) {
            return table.data.map(item => ({value: item[value], label: item[label]}));
        }
    };

    const onChangeActive = () => {
        if (!puedeEditar) return;

        if (empleado.id && !empleado.active) {
            setShowPINModal(true);
        } else {
            setActivo(!activo);
        }
    };

    const addDocumento = () => {
        setDocumentos(prev => ([
            ...prev,
            {id: documentos.length}
        ]))
    }

    useEffect(() => {
        if(empleado.documentos){
            setDocumentos(empleado.documentos)
        }
    }, []);

    const checkPINSeguridad = async () => {
		if (await validarPINSeguridad(pinSeguridad)) {
			setPinSeguridad("");
			setShowPINModal(false);
			setActivo(!activo);

            return;
		}

		toast("PIN incorrecto, intente de nuevo", "error");
	};

    const tipo = tipos?.find(t => t.nombre === cargo);

    return (<>
        <div className='form-secciones'>
            <div className='row'>
                {/* En escritorio va a la derecha; en móvil, primero: identifica al empleado. */}
                <div className='col-lg-4 order-lg-2'>
                    <Seccion titulo='Foto y estado' icono='fas fa-id-badge'>
                        <FotoField
                            foto={empleado.foto}
                            fotoCed={empleado.fotoCed}
                            name='foto'
                            readOnly={!puedeEditar}
                        />
                        <div className='form-group d-flex align-items-center justify-content-between mt-3 mb-0'>
                            <label className='label form-control-label mb-0' htmlFor='active'>
                                Activo:
                            </label>
                            <label className='switch switch-3d switch-success mb-0'>
                                <input type='hidden' name='active' value={activo} />
                                <input
                                    key={empleado.id}
                                    type='checkbox'
                                    className='switch-input'
                                    id='active'
                                    name='active'
                                    value='si'
                                    checked={activo}
                                    onChange={onChangeActive}
                                />
                                <span className='switch-label' />
                                <span className='switch-handle' />
                            </label>
                        </div>
                        <ErrorCampo errors={errors} campo='active' />
                    </Seccion>
                </div>

                <div className='col-lg-8 order-lg-1'>
                    <Seccion titulo='Datos personales' icono='fas fa-user'>
                        <div className='form-row'>
                            <div className='form-group col-12 col-md-6'>
                                <label className='label form-control-label'>
                                    Nombre:
                                </label>
                                <input
                                    type='text'
                                    className='text-input form-control'
                                    id='nombre'
                                    name='nombre'
                                    defaultValue={empleado.nombre}
                                    readOnly={!puedeEditar}
                                />
                                <ErrorCampo errors={errors} campo='nombre' />
                            </div>
                            <div className='form-group col-12 col-md-6'>
                                <label className='label form-control-label'>
                                    Apellido:
                                </label>
                                <input
                                    type='text'
                                    className='text-input form-control'
                                    id='apellido'
                                    name='apellido'
                                    defaultValue={empleado.apellido}
                                    readOnly={!puedeEditar}
                                />
                                <ErrorCampo errors={errors} campo='apellido' />
                            </div>
                            <div className='form-group col-12 col-md-6'>
                                <label className='label form-control-label'>
                                    Cédula:
                                </label>
                                <input
                                    type='text'
                                    className='text-input form-control'
                                    id='cedula'
                                    name='cedula'
                                    defaultValue={empleado.cedula}
                                    readOnly={!puedeEditar}
                                />
                                <ErrorCampo errors={errors} campo='cedula' />
                            </div>
                            <div className='form-group col-12 col-md-6'>
                                <label className='label form-control-label'>
                                    Fecha de Nacimiento:
                                </label>
                                <CampoFecha
                                    name='fecha_nacimiento'
                                    defaultValue={empleado.fechaNacimiento}
                                    readOnly={!puedeEditar}
                                />
                                <ErrorCampo errors={errors} campo='fecha_nacimiento' />
                            </div>
                            <div className='form-group col-6 col-md-4'>
                                <label className='label form-control-label'>
                                    Sexo:
                                </label>
                                <select
                                    className='text-input form-control'
                                    id='sexo'
                                    name='sexo'
                                    defaultValue={empleado.sexo}
                                    disabled={!puedeEditar}
                                >
                                    <option value="">Seleccionar...</option>
                                    <option value="M">Masculino</option>
                                    <option value="F">Femenino</option>
                                </select>
                                <ErrorCampo errors={errors} campo='sexo' />
                            </div>
                            <div className='form-group col-6 col-md-4'>
                                <label className='label form-control-label'>
                                    Tipo Sanguineo:
                                </label>
                                <select
                                    className='text-input form-control'
                                    id='grupo_sanguineo'
                                    name='grupo_sanguineo'
                                    defaultValue={empleado.grupo_sanguineo}
                                    disabled={!puedeEditar}
                                >
                                    <option value="">Seleccionar...</option>
                                    <option value="A+">A+</option>
                                    <option value="A-">A-</option>
                                    <option value="B+">B+</option>
                                    <option value="B-">B-</option>
                                    <option value="AB+">AB+</option>
                                    <option value="AB-">AB-</option>
                                    <option value="O+">O+</option>
                                    <option value="O-">O-</option>
                                </select>
                                <ErrorCampo errors={errors} campo='grupo_sanguineo' />
                            </div>
                            <div className='form-group col-12 col-md-4'>
                                <label className='label form-control-label'>
                                    Estado Civil:
                                </label>
                                <select
                                    key={empleado.id}
                                    className='text-input form-control'
                                    id='estado_civil'
                                    name='estado_civil'
                                    defaultValue={empleado.estadoCivil}
                                    disabled={!puedeEditar}>
                                    <option value='soltero'>Soltero</option>
                                    <option value='casado'>Casado</option>
                                    <option value='union-libre'>Unión Libre</option>
                                </select>
                                <ErrorCampo errors={errors} campo='estado_civil' />
                            </div>
                            <div className='form-group col-12 mb-0'>
                                <label className='label form-control-label'>
                                    Dirección:
                                </label>
                                <input
                                    type='text'
                                    className='text-input form-control'
                                    id='direccion'
                                    name='direccion'
                                    defaultValue={empleado.direccion}
                                    readOnly={!puedeEditar}
                                />
                                <ErrorCampo errors={errors} campo='direccion' />
                            </div>
                        </div>
                    </Seccion>

                    <Seccion titulo='Puesto y salario' icono='fas fa-briefcase'>
                        <div className='form-row'>
                            <div className='form-group col-12 col-md-6'>
                                <label className='label form-control-label'>
                                    Tipo Empleado:
                                </label>
                                {tipos.length > 0 && (
                                    <select
                                        key={empleado.id}
                                        className='text-input form-control'
                                        id='tipo_id'
                                        onChange={e => {
                                            onInputChange(e);
                                            const t = getTipo(e.target.value);
                                            setSueldo(t.sueldoFijo);
                                            setDescuento(t.descuento);
                                        }}
                                        defaultValue={empleado.tipoId}
                                        name='tipo_id'
                                        disabled={!puedeEditar}>
                                        <option value=''>- Seleccionar -</option>
                                        {tipos.map((tipo) => (
                                            <option key={tipo.id} value={tipo.id}>
                                                {tipo.nombre}
                                            </option>
                                        ))}
                                    </select>
                                )}
                                <ErrorCampo errors={errors} campo='tipo_id' />
                            </div>
                            <div className='form-group col-12 col-md-6'>
                                <label className='label form-control-label'>
                                    Sueldo:
                                </label>
                                <input type="hidden" name="sueldo" value={sueldo} />
                                <input type="hidden" name="descuento" value={descuento} />
                                {cargo && tipos.length > 0 && (
                                    <select
                                        key={empleado.sueldo}
                                        className='text-input form-control'
                                        id='sueldo_id'
                                        onChange={(e) => {
                                            let s = tipo.sueldos?.find(s => s.id === e.target.value);
                                            setSueldo(s ? s.sueldo_fijo : tipo.sueldoFijo);
                                            setDescuento(s ? s.descuento : tipo.descuento);
                                        }}
                                        defaultValue={empleado.sueldo_id || (tipo.sueldos?.length > 0 ? null : 1)}
                                        name='sueldo_id'
                                        disabled={!puedeEditar}>
                                        <option value=''>- Seleccionar -</option>
                                            <option value="1">{tipo.nombreSueldo || 'Predeterminado'} - {formatCurrency(tipo.sueldoFijo)} - ARS+TSS: {formatCurrency(tipo.descuento)}</option>
                                        {tipo.sueldos?.map((sueldo, index) => (
                                            <option key={sueldo.nombre} value={index + 2}>
                                                {sueldo.nombre} - {formatCurrency(sueldo.sueldo_fijo)} - ARS+TSS: {formatCurrency(sueldo.descuento)}
                                            </option>
                                        ))}
                                    </select>
                                )}
                                <ErrorCampo errors={errors} campo='sueldo' />
                            </div>
                            <div className='form-group col-12 col-md-6'>
                                <label className='label form-control-label'>
                                    Código: {codigo && <small>Último Utilizado: {codigo.ultimo}</small>}
                                </label>
                                <input
                                    type='text'
                                    className='text-input form-control'
                                    id='codigo'
                                    name='codigo'
                                    defaultValue={empleado.nombre ? empleado.codigo : codigo.mayor}
                                    readOnly={!puedeEditar}
                                />
                                <ErrorCampo errors={errors} campo='codigo' />
                            </div>
                            <div className='form-group col-12 col-md-6'>
                                <label className='label form-control-label'>Tipo:</label>
                                <select
                                    className='form-control'
                                    id='informal'
                                    name='informal'
                                    defaultValue={empleado.informal ? "si" : "no"}
                                    disabled={!puedeEditar}>
                                    <option value='no'>Formal</option>
                                    <option value='si'>Informal</option>
                                </select>
                                <ErrorCampo errors={errors} campo='informal' />
                            </div>
                            <div className='form-group col-6'>
                                <label className='label form-control-label'>
                                    Fecha Ingreso:
                                </label>
                                <CampoFecha
                                    name='fecha_ingreso'
                                    defaultValue={empleado.fechaIngreso}
                                    readOnly={!puedeEditar}
                                />
                                <ErrorCampo errors={errors} campo='fecha_ingreso' />
                            </div>
                            <div className='form-group col-6'>
                                <label className='label form-control-label'>
                                    Fecha de Salida:
                                </label>
                                <CampoFecha
                                    name='fecha_salida'
                                    defaultValue={empleado.fechaSalida}
                                    readOnly={!puedeEditar}
                                />
                                <ErrorCampo errors={errors} campo='fecha_salida' />
                            </div>
                            <div className='form-group col-12 mb-0'>
                                <label className='label form-control-label'>Categoría:</label>
                                {puedeEditar ? <SelectCreateAsync
                                    placeholder="Seleccionar..."
                                    key="categorias"
                                    id='categorias'
                                    name='categorias_empleados[]'
                                    isMulti
                                    cacheOptions
                                    defaultOptions
                                    menuPlacement="auto"
                                    defaultValue={empleado.categorias && empleado.categorias.map(c => ({label: c.nombre, value: c.id}))}
                                    loadOptions={(e) => {
                                        return getOptions("taxonomias?relacion=empleados&buscar="+e);
                                    }}
                                /> : <SoloLectura>{empleado.categorias?.map(c => c.nombre).join(', ')}</SoloLectura>}
                                <ErrorCampo errors={errors} campo='categorias_empleados' />
                            </div>
                        </div>
                    </Seccion>

                    {(cargo !== '' && operaVehiculo === 1) && (
                        <Seccion titulo='Licencia y vehículo' icono='fas fa-truck'>
                            <div className='form-row'>
                                <div className='form-group col-12 col-md-4'>
                                    <label className='label form-control-label'>
                                        Licencia:
                                    </label>
                                    <select
                                        className='text-input form-control'
                                        id='licencia'
                                        name='licencia'
                                        defaultValue={empleado.licencia}
                                        disabled={!puedeEditar}>
                                        <option value=''>- Seleccionar -</option>
                                        <option value='categoria-1'>Categoría 1</option>
                                        <option value='categoria-2'>Categoría 2</option>
                                        <option value='categoria-3'>Categoría 3</option>
                                        <option value='categoria-4'>Categoría 4</option>
                                        <option value='categoria-5'>Categoría 5</option>
                                    </select>
                                    <ErrorCampo errors={errors} campo='licencia' />
                                </div>
                                <div className='form-group col-12 col-md-4'>
                                    <label className='label form-control-label'>
                                        Expiración Licencia:
                                    </label>
                                    <CampoFecha
                                        name='licencia_expira'
                                        defaultValue={empleado.licenciaExpira}
                                        readOnly={!puedeEditar}
                                    />
                                    <ErrorCampo errors={errors} campo='licencia_expira' />
                                </div>
                                <div className='form-group col-12 col-md-4'>
                                    <label className='label form-control-label'>
                                        Vehículo Asignado:
                                    </label>
                                    <Select
                                        name='vehiculo_asignado'
                                        options={vehiculos.map(vehiculo => ({label: vehiculo.ficha, value: vehiculo.id}))}
                                        defaultValue={empleado.vehiculoAsignado ? {label: empleado.vehiculoAsignado.ficha, value: empleado.vehiculoAsignado.id} : {}}
                                        isDisabled={!puedeEditar}
                                        readOnly={!puedeEditar}
                                        menuPlacement="auto"
                                    />
                                    <ErrorCampo errors={errors} campo='vehiculo_asignado' />
                                </div>
                            </div>
                        </Seccion>
                    )}

                    <Seccion titulo='Contacto' icono='fas fa-phone'>
                        <div className='form-row'>
                            <div className='form-group col-12 col-md-4'>
                                <label className='label form-control-label'>
                                    Teléfono:
                                </label>
                                <input
                                    type='tel'
                                    className='text-input form-control'
                                    id='telefono'
                                    name='telefono'
                                    defaultValue={empleado.telefono}
                                    readOnly={!puedeEditar}
                                />
                                <ErrorCampo errors={errors} campo='telefono' />
                            </div>
                            <div className='form-group col-12 col-md-4'>
                                <label className='label form-control-label'>
                                    Flota:
                                </label>
                                <input
                                    type='tel'
                                    className='text-input form-control'
                                    id='flota'
                                    name='flota'
                                    defaultValue={empleado.flota}
                                    readOnly={!puedeEditar}
                                />
                                <ErrorCampo errors={errors} campo='flota' />
                            </div>
                            <div className='form-group col-12 col-md-4'>
                                <label className='label form-control-label'>
                                    Correo Electrónico:
                                </label>
                                <input
                                    type='email'
                                    className='text-input form-control'
                                    id='email'
                                    name='email'
                                    defaultValue={empleado.email}
                                    readOnly={!puedeEditar}
                                />
                                <ErrorCampo errors={errors} campo='email' />
                            </div>
                        </div>
                    </Seccion>

                    <Seccion titulo='Datos bancarios' icono='fas fa-university'>
                        <div className='form-row'>
                            <div className='form-group col-12 col-md-4'>
                                <label className='label form-control-label'>
                                    Institución Financiera:
                                </label>
                                {puedeEditar ? <Select
                                    isClearable
                                    options={bancos}
                                    name="institucion_financiera"
                                    menuPlacement="auto"
                                    defaultValue={empleado.institucion_financiera ? {label: empleado.institucion_financiera, value: empleado.institucion_financiera} : null}
                                /> : <SoloLectura>{empleado.institucion_financiera}</SoloLectura>}
                                <ErrorCampo errors={errors} campo='institucion_financiera' />
                            </div>
                            <div className='form-group col-12 col-md-4'>
                                <label className='label form-control-label'>Tipo de Cuenta:</label>
                                {puedeEditar ? <Select
                                    isClearable
                                    options={[{
                                        label: 'Ahorros', value: 'Ahorros'
                                    },{
                                        label: 'Corriente', value: 'Corriente'
                                    },{
                                        label: 'Ahorros-Corriente', value: 'Ahorros-Corriente'
                                    },{
                                        label: 'Otro', value: 'Otro'
                                    }]}
                                    name="tipo_cuenta"
                                    menuPlacement="auto"
                                    defaultValue={empleado.tipo_cuenta ? {label: empleado.tipo_cuenta, value: empleado.tipo_cuenta} : null}
                                /> : <SoloLectura>{empleado.tipo_cuenta}</SoloLectura>}
                                <ErrorCampo errors={errors} campo='tipo_cuenta' />
                            </div>
                            <div className='form-group col-12 col-md-4'>
                                <label className='label form-control-label'>Cuenta Bancaria:</label>
                                <input
                                    type='text'
                                    className='text-input form-control'
                                    id='cuenta_bancaria'
                                    name='cuenta_bancaria'
                                    defaultValue={empleado.cuenta_bancaria}
                                    readOnly={!puedeEditar}
                                />
                                <ErrorCampo errors={errors} campo='cuenta_bancaria' />
                            </div>
                        </div>
                    </Seccion>

                    <Seccion
                        titulo='Documentos'
                        icono='fas fa-paperclip'
                        extra={
                            puedeEditar && <button type="button" className="btn btn-link p-0" onClick={addDocumento}>
                                <i className="fas fa-plus" /> Agregar documento
                            </button>
                        }
                    >
                        {documentos.map((documento, index) => (
                            <DocumentoItem key={documento.id}
                                documento={documento}
                                existing={empleado?.documentos?.length}
                                id={index}
                                puedeEditar={puedeEditar}
                            />
                        ))}
                    </Seccion>

                    <Seccion titulo='Notas' icono='fas fa-comment'>
                        {empleado.comentarios?.length > 0 && (
                            <div className="row notas">
                                {empleado.comentarios.map(comentario => (
                                    <div className="col-md-12 nota" key={comentario.id}>
                                        <i className="zmdi zmdi-comment-text"></i>
                                        {comentario.descripcion}
                                        <div className="nota-meta">Por {comentario.user} - {dayjs(comentario.created_at).format('MM/DD/YYYY')}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className='form-group mb-0'>
                            <label className='label form-control-label' htmlFor='comentarios'>
                                Agregar nota:
                            </label>
                            <textarea
                                key={empleado.id}
                                className='text-input form-control'
                                id='comentarios'
                                name='comentarios'
                                rows='4'
                                defaultValue={empleado.notas}
                            />
                            <ErrorCampo errors={errors} campo='comentarios' />
                        </div>
                    </Seccion>
                </div>
            </div>
        </div>

        {/* <---PIN DE SEGURIDAD MODAL--- */}
        <Modal
            title="PIN de seguridad"
            show={showPINModal}
            callback={() => checkPINSeguridad()}
            acceptText="Aceptar"
            toggle={() => setShowPINModal((state) => !state)}
            size="419px"
        >
            <div className="form-group">
                <label>PIN de seguridad</label>
                <input
                    type="password"
                    className="form-control"
                    id="PIN"
                    name="pin_seguridad"
                    value={pinSeguridad}
                    onChange={(e) =>
                        setPinSeguridad(e.target.value)
                    }
                />
                <p
                    className="font-weight-bold"
                    style={{
                        color: "gray",
                        fontStyle: "italic",
                        lineHeight: "13px",
                        fontSize: "0.7rem",
                    }}
                >
                    El cliente no cuenta con credito suficiente.
                    Para continuar ingrese PIN de seguridad
                </p>
            </div>
        </Modal>
    </>);
};

export default Form;
